import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import useLanguage from '@/components/common/hooks/useLanguage';
import BottomSheet from '@/components/payout/BottomSheet';
import {
  deleteRequisites,
  RequisitePayee,
  saveRequisites,
} from '@/store/actions/payout';
import { PaymentRequisite } from '@/store/reducers/payout';

interface Props {
  payee: RequisitePayee;
  // Null when adding the first set.
  requisite: PaymentRequisite | null;
  onClose: () => void;
}

/**
 * The four fields of an IBAN transfer. Only the name and the IBAN are required:
 * a domestic transfer goes without a SWIFT code, and nothing is checked beyond
 * that -- the server is just as loose, because a rejected save is worse than a
 * typo an admin can see and fix.
 */
const RequisiteForm: React.FC<Props> = ({ payee, requisite, onClose }) => {
  const dispatch = useDispatch();
  const [{ payout, common }] = useLanguage();

  const [name, setName] = useState(requisite ? requisite.name : '');
  const [iban, setIban] = useState(requisite ? requisite.iban : '');
  const [swift, setSwift] = useState((requisite && requisite.swift) || '');
  const [country, setCountry] = useState((requisite && requisite.country) || '');
  const [isBusy, setIsBusy] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  const body = {
    name: name.trim(),
    iban: iban.trim(),
    swift: swift.trim() || undefined,
    country: country.trim() || undefined,
  };

  const isChanged = !requisite
    || body.name !== requisite.name
    || body.iban !== requisite.iban
    || body.swift !== requisite.swift
    || body.country !== requisite.country;

  // Closes once the write has landed and the section behind it has been
  // refetched. On a failure the form stays up with everything typed so far; the
  // HTTP client already toasts the server's message.
  const run = async (write: () => unknown) => {
    setIsBusy(true);

    const isDone = await (write() as Promise<boolean>);
    if (isDone) {
      onClose();
      return;
    }

    setIsBusy(false);
  };

  return (
    <BottomSheet onClose={onClose}>
      <Heading>
        {requisite ? payout.edit_payment_details : payout.add_payment_details}
      </Heading>

      <Label>{payout.requisite_name}</Label>
      <Input
        autoFocus={!requisite}
        value={name}
        placeholder={payout.requisite_name_hint}
        onChange={(e) => setName(e.target.value)}
      />

      <Label>{payout.requisite_iban}</Label>
      <Input
        isMono
        value={iban}
        spellCheck={false}
        autoCapitalize="characters"
        autoComplete="off"
        placeholder="UA00 0000 0000 0000 0000 0000 0000 0"
        onChange={(e) => setIban(e.target.value)}
      />

      <Label>{payout.requisite_swift}</Label>
      <Input
        isMono
        value={swift}
        spellCheck={false}
        autoCapitalize="characters"
        autoComplete="off"
        placeholder="UNJSUAUKXXX"
        onChange={(e) => setSwift(e.target.value)}
      />

      <Label>{payout.requisite_country}</Label>
      <Input
        value={country}
        placeholder={payout.requisite_country_hint}
        onChange={(e) => setCountry(e.target.value)}
      />

      <Buttons>
        <Secondary onClick={onClose}>{common.cancel}</Secondary>
        <Primary
          disabled={!body.name || !body.iban || !isChanged || isBusy}
          onClick={() => run(() => dispatch(saveRequisites(payee, body)))}
        >
          {payout.save}
        </Primary>
      </Buttons>

      {requisite && (
        <Actions isBusy={isBusy}>
          <RemoveLink
            onClick={() => (isConfirmingRemove
              ? run(() => dispatch(deleteRequisites(payee)))
              : setIsConfirmingRemove(true))}
          >
            {isConfirmingRemove ? payout.tap_again_to_remove : payout.remove}
          </RemoveLink>
        </Actions>
      )}
    </BottomSheet>
  );
};

const Heading = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  margin-bottom: 8px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
  margin: 14px 0 6px;
`;

const Input = styled.input<{ isMono?: boolean }>`
  width: 100%;
  height: 48px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  color: #21272e;
  outline: none;
  font-family: ${({ isMono }) => (isMono ? "'SFMono-Regular', Menlo, Consolas, monospace" : 'inherit')};

  :focus {
    border-color: #fb7e14;
  }
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 24px;
`;

const Primary = styled.button`
  flex: 1;
  height: 48px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

const Secondary = styled.button`
  flex: 1;
  height: 48px;
  margin-right: 12px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  background: #ffffff;
  color: #21272e;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const Actions = styled.div<{ isBusy: boolean }>`
  display: flex;
  margin-top: 20px;
  pointer-events: ${({ isBusy }) => (isBusy ? 'none' : 'auto')};
  opacity: ${({ isBusy }) => (isBusy ? 0.5 : 1)};
`;

const RemoveLink = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #d9534f;
  cursor: pointer;
`;

export default RequisiteForm;
